import React, {FormEvent, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState, useRootStore, useDsManager} from "@stores/AppStateContext";

import {TaskListUnitEditProps} from "../modal-names";

import {AvatarUpload} from "../../../components/AvatarUpload";
import {InputWithClear} from "../../../components/InputWithClear";
import {SideModal} from "../components/SideModal/SideModal";
import {SideModalContentBlock} from "../components/SideModal/SideModalContentBlock";
import {Textarea} from "../../../components/form/Textarea";
import {Button} from "../../../components/Button";
import {FormHint} from "../../../components/FormHint"
import {InfoIcon} from "../../../components/icons/InfoIcon";


import {TaskListUnitModel} from "@stores/taskLists/TaskListUnitModel";


export const TaskListUnitEdit = observer((props: TaskListUnitEditProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const [hasError, setError] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarTable = props.calendarTable;
    const taskListUnitEnv = useMemo(() => calendarTable.getTaskListUnitModelEnv(), [calendarTable]);

    const wsId = rootStore.view.workspace!.id;
    const taskListUnitModelProp = props.taskListUnitModel;
    const taskListUnitRef = useRef(
        taskListUnitModelProp
            ? taskListUnitModelProp
            : (
                TaskListUnitModel.create({
                    name: '',
                    workspaceId: wsId,
                    isNew: true,
                    hasChanges: true,
                }, taskListUnitEnv)
            )
    )
    const taskListUnitModel = taskListUnitRef.current;

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const isNew = taskListUnitModel.isNew;
        if (!taskListUnitModel.name.trim()) {
            setError(prev => [...prev, "EmptyName"]);
            return;
        }

        await taskListUnitModel.save();
        if (isNew) {
            calendarTable.setTaskListsUnitId(taskListUnitModel.lastSavedObject.id);
        }
        appState.setModal(null);
    }

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <SideModal
            header={taskListUnitModel.isNew
                ? intl.get('taskListUnitEdit.title_create').d('Create Location')
                : intl.get('taskListUnitEdit.title_update').d('Edit Location')
            }
            open={true}
        >
            <form onSubmit={onSubmit}>
                <SideModalContentBlock
                    header={intl.get('taskListUnitEdit.name').d('Location Name')}
                    isRequired={true}
                    description={intl.get('taskListUnitEdit.name.hint').d('Unique name that will be shown in the location selector.')}
                    mainContent={
                        <>
                            <InputWithClear
                                ref={inputRef}
                                withBackground={true}
                                Icon={null}
                                placeholder={intl.get('taskListUnitEdit.name.placeholder').d('e.g. Store #2, Main Office, Warehouse A')}
                                value={taskListUnitModel.name}
                                onChange={(val) => {
                                    taskListUnitModel.setName(val);
                                    if (hasError.includes("EmptyName") && val.trim()) {
                                        setError(prev => prev.filter(e => e !== "EmptyName"));
                                    }
                                }}
                            />
                            {hasError.includes("EmptyName") &&
                                <FormHint
                                    type={"error"}
                                    icon={<InfoIcon size={16}/>}
                                    text={intl.get('taskListUnitEdit.name.error').d('This field is required. Please enter a name for this location.')}
                                    className={"SideModalContentBlock__error"}
                                />
                            }
                        </>
                    }
                />
                <SideModalContentBlock
                    header={intl.get('common.icon').d('Icon')}
                    description={intl.get('taskListUnitEdit.icon.hint').d('Choose an icon to help visually identify this location')}
                    mainContent={
                        <AvatarUpload
                            useContextMenu={true}
                            color={dsManager!.workspacesDS.getWorkspaceColor(wsId)}
                            abbreviation={taskListUnitModel.name.charAt(0).toUpperCase()}
                            alt={taskListUnitModel.name}
                            removeAvatarCb={async () => {
                                taskListUnitModel.setAvatarPath(null);
                            }}
                            saveAvatarPath={async (avatarPath: string) => {
                                taskListUnitModel.setAvatarPath(avatarPath);
                            }}
                            avatarPath={taskListUnitModel.avatarPath ?? undefined}
                            fileKeyPart={taskListUnitModel.id}
                            size={'small'}
                        />
                    }
                />
                <SideModalContentBlock
                    header={intl.get('common.description').d('Description')}
                    description={intl.get('taskListUnitEdit.description.hint').d('For internal use — not visible to employees. Admins will be able to view and edit this in the location settings later.')}
                    mainContent={
                        <Textarea
                            value={taskListUnitModel.description!}
                            onChange={taskListUnitModel.setDescription}
                            placeholder={intl.get('taskListUnitEdit.description.placeholder').d('Optional description or notes for this location.')}
                            fixedHeight={120}
                            minHeight={120}
                        />}
                />
                <Button
                    text={taskListUnitModel.isNew
                        ? intl.get('taskListUnitEdit.locations').d("Create Location")
                        : intl.get('common.save').d("Save")
                    }
                    style={"solid"}
                    type={"primary"}
                    htmlType={'submit'}
                    loading={taskListUnitModel.isSaving}
                />
            </form>
        </SideModal>
    )
})
